<script lang="ts">
import { defineComponent, provide, ref } from 'vue';

import {
  makeDirectionProps,
  makeDisabledProps,
  makeInputProps,
  makeReadonlyProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useDirection,
  useFilteredInputProps,
  useInput,
  useProxyModel
} from '../../composable';
import { CdsInput, CdsInputLabel, CdsInputDescription } from '../CdsInput';

import type { Ref } from 'vue';

export interface IRadioButtonProvide {
  model: Ref<number | string | null | undefined>;
  isGroup: Ref<boolean>;
  disabledGroup: Ref<boolean>;
  readonlyGroup: Ref<boolean>;
  errorGroup: Ref<boolean>;
  handleChangeRadio: (value: string) => void;
}

export const radioButtonProvideDefaultValue: IRadioButtonProvide = {
  model: ref(null),
  isGroup: ref(false),
  disabledGroup: ref(false),
  readonlyGroup: ref(false),
  errorGroup: ref(false),
  handleChangeRadio: () => {}
};

export const RadioButtonKey = Symbol.for('cds:radio');
export const componentName = 'CdsRadioButton';
export default defineComponent({
  name: componentName,
  components: { CdsInput, CdsInputLabel, CdsInputDescription },
  props: {
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeDirectionProps({
      direction: 'vertical' as const
    }),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { string[] }
     */
    'update:modelValue': (value: string[]) => true
  },
  setup(props, { slots }) {
    const model = useProxyModel(props, 'modelValue', () => []);
    const { themeClasses } = provideTheme(props);
    const { hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { directionClasses } = useDirection(props, componentName);
    const { inputProps } = useFilteredInputProps(props, ['direction']);

    const handleChangeRadio = (value: string) => {
      model.value = value;
    };

    provide(RadioButtonKey, {
      model,
      isGroup: ref(true),
      disabledGroup: ref(props.disabled),
      readonlyGroup: ref(props.readonly),
      errorGroup: ref(props.error),
      handleChangeRadio
    });

    return {
      inputProps,
      hasLabel,
      hasDescription,
      directionClasses,
      themeClasses
    };
  }
});
</script>

<template>
  <cds-input :id="id" class="cds-radio-button" :class="[directionClasses, themeClasses]" v-bind="{ ...inputProps }">
    <cds-input-label v-if="hasLabel" :text="label" tag="div" class="cds-radio-button__label">
      <!--
        @slot Лейбл
        @type string | html
      -->
      <slot name="label" />
    </cds-input-label>
    <cds-input-description v-if="hasDescription" :text="description" class="cds-radio-button__description">
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </cds-input-description>
    <div class="cds-radio-button__controls">
      <!--
        @slot Слот для радио кнопок
        @type CdsRadio
      -->
      <slot />
    </div>
    <template #messages>
      <!--
        @slot Сообщения и подсказки
        @type string | html
      -->
      <slot name="messages" />
    </template>
  </cds-input>
</template>

<style lang="scss">
@import './CdsRadioButton';
</style>
